import React from 'react';
import {useCallback, useEffect} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Flex, Text} from '@priority-software/priority-style-react';
import classNames from 'classnames';
import {InputField} from './InputField';
import {Column, Record, formsStore, apiLocker} from '../../../../../../../stores/Form';
import styles from '../../styles/settingsPanel.module.scss';
import {createDataTestId} from '../../../../../../../utils/dataTestId';

const fullWidthMinLength = 40;

type DetailsContentProps = {
    enabledCols: string[];
    record: Record;
    componentClasses?: string;
};

const RecordDetails = ({
    column,
    record,
    initialValue
}: {
    column: Column;
    record: Record;
    initialValue: any;
}) => {
    if (
        column.Type !== priority.netitems.form.ColumnType.Boolean &&
        (record.parentForm.isReadonly || column.isReadonly)
    ) {
        return (
            <Text
                skin="paragraph-1"
                textColor="primary-700"
                {...createDataTestId(`panel-init-value`, true)}
            >
                {initialValue || '--'}
            </Text>
        );
    } else {
        return <InputField column={column} record={record} initialValue={initialValue} />;
    }
};

export const DetailsContent = ({enabledCols, record, componentClasses}: DetailsContentProps) => {
    const {currentForm} = formsStore;
    const currScreenLockerData = apiLocker.getDataBySession(currentForm?.session);
    const isApiLock = useCallback(() => currScreenLockerData?.locked, [currScreenLockerData]);
    const handleKeyDown = (e: KeyboardEvent) => {
        if (isApiLock()) {
            e.preventDefault();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', e => handleKeyDown(e));
        return window.removeEventListener('keydown', e => handleKeyDown(e));
    }, []);

    if (!!enabledCols.length) {
        return (
            <div className={componentClasses}>
                <Flex
                    alignItems="flex-start"
                    justifyContent="space-between"
                    wrap
                    componentClasses={styles.detailsContainer}
                    {...createDataTestId(`card-container`, true)}
                >
                    {enabledCols.map(colName => {
                        const column = record.parentForm.getColumnByName(colName);
                        const fullWidth = column.maxLength > fullWidthMinLength;
                        const initialValue = record.getField(column.id).origValue;
                        const isFieldEditable = !record.parentForm.isReadonly && !column.isReadonly;

                        return (
                            <div
                                className={classNames({
                                    [styles.detailsInput]: !column.isReadonly,
                                    [styles.halfWidth]: !fullWidth,
                                    [styles.fullWidth]: fullWidth
                                })}
                                key={colName}
                                {...createDataTestId(`card-details`, false)}
                            >
                                <Text
                                    skin="label-1"
                                    textColor={isFieldEditable ? 'primary-700' : 'secondary-500'}
                                    componentClasses={styles.colTitle}
                                >
                                    {column.Title}
                                </Text>
                                <RecordDetails
                                    column={column}
                                    record={record}
                                    initialValue={initialValue}
                                />
                            </div>
                        );
                    })}
                </Flex>
            </div>
        );
    } else {
        return null;
    }
};
